import { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import QRCode from 'qrcode';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { RefreshCw, Printer, Minus, Plus, Users, Tag, Clock } from 'lucide-react';
import { deskService, type DeskOrder } from '@/services/deskService';
import { adminRestaurantService } from '@/services/adminRestaurantService';
import { buildOrderUrl } from '@/lib/url';
import { printTickets } from '@/services/ticketPrinterService';

const QR_CODE_SIZE = 400;

interface QRCodeCache {
  [orderId: string]: string;
}

interface OrderQuantity {
  [orderId: string]: number;
}

const generateQrUrl = (providerOrderId: string): string =>
  buildOrderUrl(providerOrderId);

const formatDateTime = (dateString: string | null): string => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  return date.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
};

export function SimpleOrdersPage() {
  const [qrCodes, setQrCodes] = useState<QRCodeCache>({});
  const [printing, setPrinting] = useState<string | null>(null);
  const isPrintingRef = useRef<string | null>(null);
  const [quantities, setQuantities] = useState<OrderQuantity>({});
  const [syncInProgress, setSyncInProgress] = useState(false);

  const { data: adminRestaurant } = useQuery({
    queryKey: ['admin-restaurant'],
    queryFn: () => adminRestaurantService.getRestaurant(),
  });

  const {
    data: ordersData,
    isLoading,
    refetch,
    isRefetching,
  } = useQuery({
    queryKey: ['admin-orders', 'current'],
    queryFn: () => deskService.getOrders('current'),
    refetchInterval: 10000,
  });

  const orders = useMemo(() => ordersData?.orders || [], [ordersData]);

  const syncMutation = useMutation({
    mutationFn: () => deskService.syncOrders(),
    onSuccess: () => {
      setSyncInProgress(true);
      toast.loading('Sincronización iniciada...', { id: 'sync' });
    },
    onError: () => {
      toast.error('Error al iniciar sincronización');
    },
  });

  const { data: syncStatus } = useQuery({
    queryKey: ['sync-status'],
    queryFn: () => deskService.getSyncStatus(),
    enabled: syncInProgress,
    refetchInterval: 2000,
  });

  useEffect(() => {
    if (!syncStatus) return;

    if (syncStatus.status === 'running') {
      const synced = syncStatus.synced || 0;
      const total = syncStatus.total || 0;
      toast.loading(`Sincronizando: ${synced}/${total} órdenes`, { id: 'sync' });
    } else if (syncStatus.status === 'completed') {
      const synced = syncStatus.synced || 0;
      toast.success(`Sincronización completada: ${synced} órdenes`, { id: 'sync', duration: 4000 });
      setSyncInProgress(false);
      refetch();
    } else if (syncStatus.status === 'failed') {
      toast.error('Error en la sincronización', { id: 'sync' });
      setSyncInProgress(false);
    }
  }, [syncStatus, refetch]);

  const generateQrCodesForOrders = useCallback(
    async (orderList: DeskOrder[]) => {
      for (const order of orderList) {
        if (qrCodes[order.id]) continue;

        try {
          const qrUrl = generateQrUrl(order.provider_order_id);
          const qrDataUrl = await QRCode.toDataURL(qrUrl, {
            width: QR_CODE_SIZE,
            margin: 1,
            color: { dark: '#000000', light: '#FFFFFF' },
          });
          setQrCodes((prev) => ({ ...prev, [order.id]: qrDataUrl }));
        } catch (err) {
          console.error(`Error generating QR for order ${order.id}:`, err);
        }
      }
    },
    [qrCodes]
  );

  useEffect(() => {
    if (orders.length > 0) {
      generateQrCodesForOrders(orders);
    }
  }, [orders, generateQrCodesForOrders]);

  const getQuantity = (orderId: string): number => quantities[orderId] || 1;

  const incrementQuantity = (orderId: string) => {
    setQuantities((prev) => ({
      ...prev,
      [orderId]: Math.min((prev[orderId] || 1) + 1, 10),
    }));
  };

  const decrementQuantity = (orderId: string) => {
    setQuantities((prev) => ({
      ...prev,
      [orderId]: Math.max((prev[orderId] || 1) - 1, 1),
    }));
  };

  const countOnboardedItems = (order: DeskOrder): number => {
    return order.line_items?.filter((item) => item.onboarded).length || 0;
  };

  const handlePrint = async (order: DeskOrder) => {
    if (isPrintingRef.current) return;
    isPrintingRef.current = order.id;

    const onboardedCount = countOnboardedItems(order);
    if (onboardedCount === 0) {
      toast.error('Esta orden no tiene items con foto');
      isPrintingRef.current = null;
      return;
    }

    const count = getQuantity(order.id);
    setPrinting(order.id);

    try {
      await deskService.printOrder(order.id, count);

      if (!qrCodes[order.id]) {
        toast.error('El código QR aún no está listo');
        return;
      }

      const result = await printTickets(
        { qrCodeUrl: qrCodes[order.id], logoUrl: adminRestaurant?.logo_url },
        {
          count,
          onProgress: (current, total) => {
            toast.info(`Imprimiendo ticket ${current}/${total}...`);
          }
        }
      );

      if (result.failed > 0) {
        toast.error(`Error al imprimir ${result.failed} ticket(s). ${result.success} impresos correctamente.`);
      } else {
        toast.success(`${count} QR${count > 1 ? 's' : ''} enviado${count > 1 ? 's' : ''} a la impresora`);
      }
    } catch (err) {
      console.error('Print error:', err);
      toast.error('Error al imprimir');
    } finally {
      setPrinting(null);
      isPrintingRef.current = null;
    }
  };

  if (isLoading && orders.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 p-4">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="bg-white rounded-lg p-4 animate-pulse">
                <Skeleton className="h-6 w-20 mb-3" />
                <Skeleton className="h-4 w-16 mb-4" />
                <Skeleton className="h-24 w-24 mx-auto mb-4" />
                <Skeleton className="h-10 w-full" />
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="sticky top-0 z-10 bg-white border-b px-4 py-3">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div>
            <h1 className="text-lg font-medium text-gray-900">Órdenes</h1>
            <p className="text-xs text-gray-500">{orders.length} activas</p>
          </div>
          <Button
            size="sm"
            onClick={() => syncMutation.mutate()}
            disabled={syncMutation.isPending || isRefetching}
            variant="outline"
          >
            <RefreshCw className={`mr-1.5 h-4 w-4 ${syncMutation.isPending || isRefetching ? 'animate-spin' : ''}`} />
            Actualizar
          </Button>
        </div>
      </div>

      <div className="p-4 max-w-7xl mx-auto">
        {orders.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500">No hay órdenes activas</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
            {orders.map((order) => {
              const onboardedCount = countOnboardedItems(order);
              const hasMemory = order.memory !== null;
              const printCount = order.memory?.print_count || 0;

              return (
                <div
                  key={order.id}
                  className="bg-white rounded-lg shadow-sm border border-gray-200 p-3 flex flex-col"
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 mb-1">
                        {order.identification.ticket_name ? (
                          <Badge className="bg-content-primary text-white text-xs px-2 py-0.5 truncate max-w-[120px]">
                            <Tag className="w-3 h-3 mr-1 flex-shrink-0" />
                            <span className="truncate">{order.identification.ticket_name}</span>
                          </Badge>
                        ) : (
                          <span className="text-sm font-medium text-gray-700 truncate">
                            #{order.provider_order_id.slice(-6)}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2 text-xs text-gray-500">
                        {order.created_at && (
                          <span className="flex items-center gap-0.5">
                            <Clock className="w-3 h-3" />
                            {formatDateTime(order.created_at)}
                          </span>
                        )}
                        {order.identification.guests_count && (
                          <span className="flex items-center gap-0.5">
                            <Users className="w-3 h-3" />
                            {order.identification.guests_count}
                          </span>
                        )}
                      </div>
                    </div>
                    <span
                      className={`text-xs px-1.5 py-0.5 rounded ${
                        hasMemory
                          ? 'bg-content-primary text-white'
                          : 'bg-gray-100 text-gray-600'
                      }`}
                    >
                      {hasMemory ? `${printCount}x` : '-'}
                    </span>
                  </div>

                  <div className="flex-1 flex items-center justify-center my-2">
                    {qrCodes[order.id] ? (
                      <img
                        src={qrCodes[order.id]}
                        alt="QR"
                        className="w-20 h-20 rounded"
                      />
                    ) : (
                      <div className="w-20 h-20 bg-gray-100 rounded flex items-center justify-center">
                        <RefreshCw className="w-6 h-6 text-gray-400 animate-spin" />
                      </div>
                    )}
                  </div>

                  <div className="flex items-center justify-center gap-2 mb-2">
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-8 w-8 p-0"
                      onClick={() => decrementQuantity(order.id)}
                      disabled={getQuantity(order.id) <= 1}
                    >
                      <Minus className="h-4 w-4" />
                    </Button>
                    <span className="w-8 text-center font-medium text-sm">
                      {getQuantity(order.id)}
                    </span>
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-8 w-8 p-0"
                      onClick={() => incrementQuantity(order.id)}
                      disabled={getQuantity(order.id) >= 10}
                    >
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>

                  <Button
                    size="sm"
                    className="w-full min-h-11"
                    onClick={() => handlePrint(order)}
                    disabled={printing === order.id || !qrCodes[order.id] || onboardedCount === 0}
                  >
                    <Printer className="mr-1.5 h-4 w-4" />
                    {printing === order.id ? '...' : 'Imprimir'}
                  </Button>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
